import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TutorialVideo } from '../types';

interface VideoPlayerModalProps {
    video: TutorialVideo;
    onClose: () => void;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({ video, onClose }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [streamUrl, setStreamUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const sessionId = useRef(`sess-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`);
    const trackingInterval = useRef<ReturnType<typeof setInterval> | null>(null);

    // Resolve video URL: prefer direct public URL, fallback to SAS stream
    useEffect(() => {
        const directUrl = video.video_url || video.blob_url;
        if (directUrl) {
            // Öffentliche URL direkt verwenden
            setStreamUrl(directUrl);
            setLoading(false);
        } else {
            // Legacy Fallback: SAS-URL über API holen
            (async () => {
                try {
                    const res = await fetch(`/api/tutorials/${video.id}/stream`);
                    if (!res.ok) throw new Error('Stream URL nicht verfügbar');
                    const data = await res.json();
                    setStreamUrl(data.url);
                } catch (e: any) {
                    setError(e.message);
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [video.id, video.video_url, video.blob_url]);

    // Track view progress
    const sendViewUpdate = useCallback(async (completed = false) => {
        const el = videoRef.current;
        if (!el) return;
        try {
            await fetch('/api/tutorial-views', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    video_id: video.id,
                    session_id: sessionId.current,
                    watch_duration_seconds: Math.round(el.currentTime),
                    completed,
                    max_position_seconds: Math.round(el.currentTime),
                }),
            });
        } catch { /* non-critical */ }
    }, [video.id]);

    // Start tracking on play
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;

        const onPlay = () => {
            if (trackingInterval.current) clearInterval(trackingInterval.current);
            trackingInterval.current = setInterval(() => sendViewUpdate(false), 5000);
        };
        const onPause = () => {
            if (trackingInterval.current) clearInterval(trackingInterval.current);
            sendViewUpdate(false);
        };
        const onEnded = () => {
            if (trackingInterval.current) clearInterval(trackingInterval.current);
            sendViewUpdate(true);
        };

        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        el.addEventListener('ended', onEnded);

        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('ended', onEnded);
            if (trackingInterval.current) clearInterval(trackingInterval.current);
            sendViewUpdate(false); // Final update on unmount
        };
    }, [streamUrl, sendViewUpdate]);

    // Close on Escape
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const modalContent = (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={onClose}>
            <div
                className="relative w-full max-w-5xl mx-4 bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/5">
                    <div>
                        <h3 className="text-white font-bold text-lg">{video.title}</h3>
                        <span className="text-xs text-slate-400">{video.category}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/20 flex items-center justify-center text-white transition-all text-xl"
                    >
                        ✕
                    </button>
                </div>

                {/* Video */}
                <div className="relative aspect-video bg-black">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}
                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">
                            ⚠️ {error}
                        </div>
                    )}
                    {streamUrl && (streamUrl.includes('youtube.com') || streamUrl.includes('youtu.be')) ? (
                        <iframe
                            title="YouTube Video Tutorial"
                            className="w-full h-full"
                            src={`https://www.youtube.com/embed/${(() => {
                                const match = streamUrl.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/);
                                return (match && match[2].length === 11) ? match[2] : '';
                            })()}?autoplay=1`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            onLoad={() => setLoading(false)}
                        ></iframe>
                    ) : (streamUrl && (
                        <video
                            ref={videoRef}
                            src={streamUrl}
                            controls
                            autoPlay
                            playsInline
                            className="w-full h-full"
                            onLoadedData={() => setLoading(false)}
                            onError={() => { setError('Video konnte nicht geladen werden'); setLoading(false); }}
                        />
                    ))}
                </div>

                {/* Description */}
                {video.description && (
                    <div className="p-4 text-sm text-slate-400 border-t border-white/5">
                        {video.description}
                    </div>
                )}
            </div>
        </div>
    );

    if (typeof document === 'undefined') return modalContent;
    return createPortal(modalContent, document.body);
};

export default VideoPlayerModal;
